import { createDefaultImageReader, createDefaultImageWriter, processImage } from '@pqina/pintura';
import { LocaleCode, translation } from './translation';
import { FieldError, Image as ImageTyp } from './ui'; // alias `Image` to prevent bundling conflicts with `new Image()`, which is the Image of the browser.

const MEGABYTE = 1024 * 1024; // 1 MB
const MAX_SERVER_IMAGE_SIZE = 3 * MEGABYTE; // 3 MB, the max size accepted by the server for merchant, category and topic images, processed synchronously via Lambda, capped by its 6 MB payload limit.
const MAX_SERVER_PRODUCT_IMAGE_SIZE = 5 * MEGABYTE; // 5 MB, the max size accepted by the server for product images, processed asynchronously via S3-triggered Lambda, so not bound by the synchronous payload limit.
const MAX_CLIENT_IMAGE_SIZE = 20 * MEGABYTE; // 20 MB, the max size accepted by the client for every uploaded image. Images will then be transformed client-side to satisfy the max size accepted by the server.

// Intentionally high: a false positive (original sent at 1.0 quality)
// is harmless (slightly larger upload), while a false negative
// (pre-compressed image re-compressed) degrades quality and
// introduces visible artifacts.
const PROBE_REDUCTION_THRESHOLD = 0.5;
const PROBE_QUALITY = 0.9;

const MERCHANT_LOGO_MIN_WIDTH = 250;
const MERCHANT_LOGO_MIN_HEIGHT = 250;
const MERCHANT_COVER_MIN_WIDTH = 1500;
const MERCHANT_COVER_MIN_HEIGHT = 300;

// Image min side:
// - 800px is enforced client-side during manual image uploads
// - 500px is the threshold for non-manual uploads such as imports via
//   link
const PRODUCT_IMAGE_MIN_WIDTH = 500;
const PRODUCT_IMAGE_MIN_HEIGHT = 500;
const PRODUCT_IMAGE_UPLOAD_MIN_WIDTH = 800;
const PRODUCT_IMAGE_UPLOAD_MIN_HEIGHT = 800;

const PRODUCT_IMAGE_LARGE_WIDTH = 2400;
const PRODUCT_IMAGE_LARGE_HEIGHT = 2400;
const TOPIC_IMAGE_MIN_WIDTH = 200;
const TOPIC_IMAGE_MIN_HEIGHT = 200;
const CATEGORY_IMAGE_MIN_WIDTH = 200;
const CATEGORY_IMAGE_MIN_HEIGHT = 200;

type MerchantImageType = 'logo' | 'cover';

interface imageResolution {
	width: number;
	height: number;
}

const transformMerchantImage = async (file: File, locale: LocaleCode): Promise<File> => {
	return transformImage(file, locale, 0.7);
};

const transformProductImage = async (file: File, locale: LocaleCode): Promise<File> => {
	const t = translation[locale];
	let r: imageResolution;
	try {
		r = await getImageResolution(file);
	} catch (err) {
		throw new Error(
			t[
				'Image {name} could not be processed. Please try again and if necessary try using another image'
			].replace('{name}', String(file.name)),
		);
	}
	let targetSize;
	if (r.width > PRODUCT_IMAGE_LARGE_WIDTH || r.height > PRODUCT_IMAGE_LARGE_HEIGHT) {
		targetSize = {
			width: PRODUCT_IMAGE_LARGE_WIDTH,
			height: PRODUCT_IMAGE_LARGE_HEIGHT,
		};
	}

	// Probe: compress at quality 0.9 and measure how much the file
	// shrinks. If the reduction is small, the image was likely
	// already compressed, so we send it at quality 1.0 to avoid
	// stacking lossy compression artifacts.
	const probeResult = await transformImage(file, locale, PROBE_QUALITY, targetSize, true);
	const sizeReduction = 1 - probeResult.size / file.size;

	if (sizeReduction > PROBE_REDUCTION_THRESHOLD) {
		// High-quality original — 0.9 is a good trade-off.
		return probeResult;
	}

	// Already compressed — try quality 1.0 to avoid additional
	// artifacts. If for some reason 1.0 exceeds the server size
	// limit, fall back to the 0.9 probe result which is already
	// validated.
	try {
		return await transformImage(file, locale, 1.0, targetSize, true);
	} catch {
		return probeResult;
	}
};

const transformTopicImage = async (file: File, locale: LocaleCode): Promise<File> => {
	return transformImage(file, locale, 0.7, {
		width: TOPIC_IMAGE_MIN_WIDTH,
		height: TOPIC_IMAGE_MIN_HEIGHT,
	});
};

const transformCategoryImage = async (file: File, locale: LocaleCode): Promise<File> => {
	return transformImage(file, locale, 0.7, {
		width: CATEGORY_IMAGE_MIN_WIDTH,
		height: CATEGORY_IMAGE_MIN_HEIGHT,
	});
};

const transformImage = async (
	file: File,
	locale: LocaleCode,
	quality: number, // a floating-point number from 0.0 to 1.0.
	targetSize?: imageResolution,
	isProduct: boolean = false,
): Promise<File> => {
	const t = translation[locale];

	let processed: File;
	try {
		const { dest } = await processImage(file, {
			imageReader: createDefaultImageReader(),
			imageWriter: createDefaultImageWriter({
				mimeType: 'image/webp',
				quality: quality,
				targetSize: targetSize,
			}),
		});
		processed = dest;
	} catch (err) {
		console.error(`Error while processing image "${file.name}": ${err}`);
		throw new Error(
			t[
				'Image {name} could not be processed. Please try again and if necessary try using another image'
			].replace('{name}', String(file.name)),
		);
	}

	// If the browser doesn't support WebP encoding, Pintura silently returns
	// the image in a different format. Fall back to PNG, which is universally
	// supported and lossless — the server-side Lambda converts it to WebP anyway.
	if (processed.type !== 'image/webp') {
		console.warn(
			`Pintura returned ${processed.type} instead of image/webp for "${file.name}". Trying PNG fallback.`,
		);
		try {
			const { dest: pngDest } = await processImage(file, {
				imageReader: createDefaultImageReader(),
				imageWriter: createDefaultImageWriter({
					mimeType: 'image/png',
					targetSize: targetSize,
				}),
			});
			if (pngDest.type !== 'image/png') {
				throw new Error('not png');
			}
			processed = pngDest;
		} catch {
			console.error(
				`Pintura PNG fallback also failed for "${file.name}". The browser likely does not support any modern image encoding.`,
			);
			throw new Error(
				t[
					'Image {name} could not be processed. Your device or browser may be too old. Please update it or try from a different device'
				].replace('{name}', String(file.name)),
			);
		}
	}

	validateImageFileSize(processed, false, locale, isProduct);
	return processed;
};

const validateImage = async (
	image: ImageTyp | undefined,
	locale: LocaleCode,
	fieldName: string,
	minWidth: number,
	minHeight: number,
	isRequired: boolean,
	validateOneSide: boolean,
	isProduct: boolean = false,
) => {
	const t = translation[locale];

	if (image === undefined) {
		if (!isRequired) {
			return;
		}
		throw new FieldError(t['Image is required'], fieldName);
	}

	if (image.file != null) {
		try {
			await validateImageFile(
				image.file!,
				minWidth,
				minHeight,
				validateOneSide,
				false,
				locale,
				isProduct,
			);
		} catch (err) {
			const e = err as Error;
			throw new FieldError(e.message, fieldName);
		}
	}
};

const validateImageList = async (
	list: ImageTyp[] | undefined,
	locale: LocaleCode,
	fieldName: string,
	minWidth: number,
	minHeight: number,
	minLength: number,
	maxLength: number,
	isRequired: boolean,
	validateOneSide: boolean,
	isProduct: boolean = false,
) => {
	const t = translation[locale];

	if (list === undefined) {
		if (!isRequired) {
			return;
		}
		throw new FieldError(t['You must upload at least one image'], fieldName);
	}

	if (list.length < minLength) {
		if (minLength === 1) {
			throw new FieldError(t['You must upload at least one image'], fieldName);
		} else {
			throw new FieldError(
				t['You must upload at least {min} images'].replace('{min}', String(minLength)),
				fieldName,
			);
		}
	}

	if (list.length > maxLength) {
		throw new FieldError(
			t['You can upload at most {max} images'].replace('{max}', String(maxLength)),
			fieldName,
		);
	}

	for (const image of list) {
		if (image.file != null) {
			try {
				await validateImageFile(
					image.file!,
					minWidth,
					minHeight,
					validateOneSide,
					false,
					locale,
					isProduct,
				);
			} catch (err) {
				const e = err as Error;
				throw new FieldError(e.message, fieldName);
			}
		}
	}
};

const validateImageFile = async (
	file: File,
	minWidth: number,
	minHeight: number,
	validateOneSide: boolean,
	isClient: boolean,
	locale: LocaleCode,
	isProduct: boolean = false,
) => {
	const t = translation[locale];

	if (file.type !== 'image/jpeg' && file.type !== 'image/png' && file.type !== 'image/webp') {
		throw new Error(
			t['Image must be in jpeg, png or webp format but {name} is in {format} format']
				.replace('{name}', String(file.name))
				.replace('{format}', file.type.split('/')[1]),
		);
	}

	try {
		validateImageFileSize(file, isClient, locale, isProduct);
	} catch (err) {
		throw err;
	}

	let r: imageResolution;
	try {
		r = await getImageResolution(file);
	} catch (err) {
		throw new Error(
			t[
				'Image {name} could not be processed. Please try again and if necessary try using another image'
			].replace('{name}', String(file.name)),
		);
	}

	if (validateOneSide) {
		if (r.width < minWidth && r.height < minHeight) {
			throw new Error(
				t[
					'Image must have at least one side that is bigger than {size} but the longest side of {name} is {size2}'
				]
					.replace('{size}', String(minWidth) + 'px')
					.replace('{name}', String(file.name))
					.replace('{size2}', String(Math.max(r.width, r.height)) + 'px'),
			);
		}
	} else {
		if (r.width < minWidth) {
			throw new Error(
				t['Image width must be at least {size} but {name} width is {size2}']
					.replace('{size}', String(minWidth) + 'px')
					.replace('{name}', String(file.name))
					.replace('{size2}', String(r.width) + 'px'),
			);
		}
		if (r.height < minHeight) {
			throw new Error(
				t['Image height must be at least {size} but {name} height is {size2}']
					.replace('{size}', String(minHeight) + 'px')
					.replace('{name}', String(file.name))
					.replace('{size2}', String(r.height) + 'px'),
			);
		}
	}
};

const validateImageFileSize = (
	file: File,
	isClient: boolean,
	locale: LocaleCode,
	isProduct: boolean = false,
) => {
	const t = translation[locale];

	let maxSize;
	if (isClient) {
		maxSize = MAX_CLIENT_IMAGE_SIZE;
	} else if (isProduct) {
		maxSize = MAX_SERVER_PRODUCT_IMAGE_SIZE;
	} else {
		maxSize = MAX_SERVER_IMAGE_SIZE;
	}

	if (file.size > maxSize) {
		if (isClient) {
			throw new Error(
				t['Image must be smaller than {size} but {name} is {size2}']
					.replace('{size}', `${maxSize / MEGABYTE}MB`)
					.replace('{name}', String(file.name))
					.replace('{size2}', `${Math.ceil(file.size / MEGABYTE)}MB`),
			);
		} else {
			throw new Error(
				t['Image {name} is too big. Please try again with a smaller image'].replace(
					'{name}',
					file.name,
				),
			);
		}
	}
};

const getImageResolution = (file: File): Promise<imageResolution> => {
	return new Promise((resolve, reject) => {
		const img = new Image();

		img.src = URL.createObjectURL(file);

		img.onload = () => {
			resolve({ width: img.width, height: img.height });
			URL.revokeObjectURL(img.src);
		};

		img.onerror = () => {
			reject(new Error('Error while loading the image'));
		};
	});
};

export type { MerchantImageType };
export {
	MERCHANT_LOGO_MIN_WIDTH,
	MERCHANT_LOGO_MIN_HEIGHT,
	MERCHANT_COVER_MIN_WIDTH,
	MERCHANT_COVER_MIN_HEIGHT,
	PRODUCT_IMAGE_MIN_WIDTH,
	PRODUCT_IMAGE_MIN_HEIGHT,
	PRODUCT_IMAGE_UPLOAD_MIN_WIDTH,
	PRODUCT_IMAGE_UPLOAD_MIN_HEIGHT,
	TOPIC_IMAGE_MIN_WIDTH,
	TOPIC_IMAGE_MIN_HEIGHT,
	CATEGORY_IMAGE_MIN_WIDTH,
	CATEGORY_IMAGE_MIN_HEIGHT,
	transformMerchantImage,
	transformProductImage,
	transformTopicImage,
	transformCategoryImage,
	validateImage,
	validateImageList,
	validateImageFile,
};
